import React, { useState, useEffect } from "react";
import { useTranslation } from 'react-i18next';
import axios from "../../axiosConfig";
import "../../styles/Tabs.css"; // Import the CSS file

const Tabs = ({ tabs, activeId, initialTab }) => {
    const { t } = useTranslation('common');
    const [activeTab, setActiveTab] = useState(
        (initialTab && tabs.some(t => t.key === initialTab)) ? initialTab : (tabs[0]?.key || "")
    );
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [page, setPage] = useState(0);
    const [totalPages, setTotalPages] = useState(1);
    const [pageSize, setPageSize] = useState(10);

    // Sync activeTab when initialTab changes (e.g. URL query param update)
    useEffect(() => {
        if (initialTab && tabs.some(t => t.key === initialTab)) {
            setActiveTab(initialTab);
        }
    }, [initialTab, tabs]);

    useEffect(() => {
        if (!activeTab) return;

        const tabConfig = tabs.find((tab) => tab.key === activeTab);
        if (!tabConfig || tabConfig.component) return; // Skip data fetching for component-based tabs

        setLoading(true);
        setError(null);
        setData(null); // Clear previous tab's data immediately

        axios
            .get(tabConfig.endpoint, {
                headers: { Authorization: `Bearer ${localStorage.getItem("jwt")}` },
                params: tabConfig.paginated ? { page, size: pageSize } : {},
            })
            .then((response) => {
                // Check if response data exists before processing
                if (response.data) {
                    // Handle both paginated and non-paginated responses
                    setData(tabConfig.paginated ? response.data.content : response.data);
                    setTotalPages(tabConfig.paginated ? response.data.totalPages : 1);
                } else {
                    // Handle empty responses
                    setData([]);
                    setTotalPages(1);
                }
                setLoading(false);
            })
            .catch((err) => {
                console.error("Error fetching data:", err);
                setError(t('tabs.fetchError'));
                setLoading(false);
            });
    }, [activeTab, activeId, page, pageSize]);

    const handleTabClick = (tabKey) => {
        setActiveTab(tabKey);
        setPage(0); // Reset page number when switching tabs
        setData(null); // Clear data when switching tabs
    };

    // Find the current tab config for rendering
    const currentTabConfig = tabs.find((tab) => tab.key === activeTab);

    return (
        <div className="tabs-container">
            {/* Tab Navigation */}
            <div className="tabs-nav">
                {tabs.map((tab) => (
                    <button
                        key={tab.key}
                        onClick={() => handleTabClick(tab.key)}
                        className={`tab-button ${activeTab === tab.key ? 'active' : ''}`}
                    >
                        {tab.label}
                    </button>
                ))}
            </div>

            {/* Content Area */}
            <div className="tab-content-area">
                {/* Component-based tab - render directly */}
                {currentTabConfig?.component && (
                    <div className="tab-content">
                        {currentTabConfig.component}
                    </div>
                )}

                {/* Data-fetching tab - existing behavior */}
                {!currentTabConfig?.component && (
                    <>
                        {loading && <p className="tab-loading">{t('tabs.loading')}</p>}
                        {error && <p className="tab-error">{error}</p>}
                        {!loading && !error && data !== null && currentTabConfig && (
                            <div className="tab-content">
                                {currentTabConfig.renderContent(data)}
                            </div>
                        )}
                    </>
                )}

                {/* Pagination Controls (only if paginated) */}
                {currentTabConfig?.paginated && totalPages > 1 && (
                    <div className="tab-pagination">
                        <button
                            className="tab-pagination-btn"
                            disabled={page === 0}
                            onClick={() => setPage((prev) => prev - 1)}
                        >
                            {t('tabs.previous')}
                        </button>
                        <span className="tab-pagination-info">
                            {t('tabs.pageOf', { current: page + 1, total: totalPages })}
                        </span>
                        <button
                            className="tab-pagination-btn"
                            disabled={page + 1 >= totalPages}
                            onClick={() => setPage((prev) => prev + 1)}
                        >
                            {t('tabs.next')}
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
};

export default Tabs;